import { gql } from "@apollo/client";
import { alpha, ButtonBase } from "@mui/material";
import Stack from "atoms/Stack";
import { COLOR_SECONDARY, COLOR_WHITE } from "helpers/colors";
import formatTimeString from "helpers/formatTime";
import { gaEvent } from "helpers/gtag";
import useData from "helpers/useData";
import { Star } from "mdi-material-ui";
import EventAvatar, { EventAvatarFragment } from "molecules/EventAvatar";
import EventSubjectLabel, { EventSubjectLabelFragment } from "molecules/EventSubjectLabel";
import { videoPageContext } from "pages/VideoPage";
import React, { useContext, useEffect, useMemo } from "react";

import EventActions, { EventActionsFragment } from "./EventActions";
import VideoEventPotwRating, { VideoEventPotwRatingFragment } from "./VideoEventPotwRating";
import { videoPageLayoutContext } from "./VideoPageLayout";

export const VideoEventsFragment = gql`
  fragment VideoEventsFragment on Game {
    id
    ...EventActionsFragment
    ...VideoEventPotwRatingFragment
    events {
      id
      startAtSeconds
      occurredAtSeconds
      humanName
      type
      ...EventSubjectLabelFragment
      ...EventAvatarFragment
    }
  }
  ${VideoEventPotwRatingFragment}
  ${EventSubjectLabelFragment}
  ${EventActionsFragment}
  ${EventAvatarFragment}
`;

export default function VideoEvents({ game }) {
  const { activeEventId, filteredEventIds, videoRef } = useContext(videoPageContext);
  const { sidebarOpen, isPortrait } = useContext(videoPageLayoutContext);
  const eventElements = useMemo(() => ({}), []);
  useEffect(() => {
    const activeEventElement = eventElements[activeEventId];
    if (activeEventElement && sidebarOpen) {
      activeEventElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeEventId]);

  return (
    <>
      {game?.events
        .filter((e) => filteredEventIds.includes(e.id))
        .map((event) => (
          <Stack
            key={event.id}
            element={<ButtonBase component="div" tabIndex={-1} />}
            ref={(el) => {
              eventElements[event.id] = el;
            }}
            padding
            dense
            style={{
              fontSize: 12,
              borderBottom: `1px solid ${alpha(COLOR_WHITE, 0.1)}`,
              ...(activeEventId === event.id && {
                backgroundColor: alpha(COLOR_WHITE, 0.1),
              }),
            }}
          >
            <Stack horizontal dense>
              <Stack
                dense
                horizontal
                alignItemsCenter
                element={
                  <ButtonBase
                    disableRipple
                    style={{ textAlign: "start", flex: 1 }}
                    onClick={() => {
                      if (videoRef.current) {
                        videoRef.current.currentTime = event.startAtSeconds;
                      }
                      gaEvent("game_highlight_select", {
                        event_type: event.type,
                      });
                    }}
                  />
                }
              >
                <div style={{ opacity: 0.6 }}>{formatTimeString(event.occurredAtSeconds)}</div>
                <EventAvatar
                  inverse
                  event={event}
                  size={32}
                  active={activeEventId === event.id}
                  useHomeAwayTeamColors
                />
                <Stack veryDense>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: 14,
                    }}
                  >
                    <EventSubjectLabel event={event} />
                  </div>
                  <Stack
                    veryDense
                    horizontal
                    alignItemsCenter
                    style={{
                      opacity: 0.6,
                      fontWeight: "bold",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ textTransform: "uppercase" }}>{event.humanName}</div>
                    <EventPotwStar eventId={event.id} />
                  </Stack>
                </Stack>
              </Stack>
              {activeEventId === event.id && isPortrait && (
                <VideoEventPotwRating game={game} eventId={event.id} mobileEventStream />
              )}
            </Stack>
            {activeEventId === event.id && (
              <Stack veryDense horizontal alignItemsCenter>
                <EventActions game={game} eventId={event.id} />
              </Stack>
            )}
          </Stack>
        ))}
    </>
  );
}

function EventPotwStar({ eventId }) {
  const [data] = useData(
    gql`
      query EventPotwStar($eventId: ID!) {
        event(id: $eventId) {
          id
          potwEntriesAny
        }
      }
    `,
    { eventId },
  );

  return data?.event?.potwEntriesAny && <Star fontSize="inherit" style={{ color: COLOR_SECONDARY }} />;
}
